import { useEffect, useMemo } from 'react'
import { Divider, Grid } from 'semantic-ui-react'
import currency from 'currency.js'
import '../style.scss'

import {
  Button,
  Card,
  GridRowColumn,
  Text,
} from '../../../../components/BaseComponents'
import {
  DeviceWidth,
  useIsDeviceWidth,
} from '../../../../utils/deviceWidthHelpers'
import { getProductByInterval, productPriceAsCurrency } from '../helpers'
import { StripeProduct } from '../../../../actions/settings/billingActions'
import { StripeInterval } from '../../../../reducers/subscription.slice'
import PlanDetailsSection from './components/PlanDetailsSection'
import { FREE_TRIAL_DAYS, FREE_TRIAL_PROMO } from '../../helpers'
import { formatCurrency } from '../../../../utils/currencyHelpers'
import { useCatchupBookkeepingPrice } from '../hooks'
import CatchupBookkeepingSection from './components/CatchupBookkeepingSection'
import { useFetchResponse, useReselector } from '../../../../utils/sharedHooks'
import { fetchCatchUpBKCoupon } from '../../../CatchUpBookkeepingStatus/catchUpBookkeepingStatus.slice'
import { selectHasAnnualCommitment } from '../../../SelfServeCancellationFlow/userCancellation.selectors'
import { useAnalyticsTrack } from '@/featuresV1/Amplitude'

const StripeProductCard = ({
  stripeProducts,
  selectedProduct,
  onSelect,
  freeTrialEnabled,
  basicPlanEnabled,
  isLateJoiner,
  freeTrialSearchParamValue,
  basicPlanSearchParamValue,
}: {
  stripeProducts: StripeProduct[]
  selectedProduct?: StripeProduct
  onSelect: (val: StripeProduct) => void
  freeTrialEnabled: boolean
  basicPlanEnabled: boolean
  isLateJoiner: boolean | null | undefined
  freeTrialSearchParamValue: string | null | undefined
  basicPlanSearchParamValue: string | null | undefined
}) => {
  const track = useAnalyticsTrack()
  const isMobile = useIsDeviceWidth(DeviceWidth.mobile)
  const annualPlan = getProductByInterval(stripeProducts, StripeInterval.year)
  const monthlyPlan = getProductByInterval(stripeProducts, StripeInterval.month)
  const isAnnual = selectedProduct !== monthlyPlan
  const catchUpBKCoupon = useFetchResponse(
    fetchCatchUpBKCoupon,
    isAnnual,
    basicPlanEnabled
  )
  const hasAnnualCommitment = useReselector(selectHasAnnualCommitment)

  const CARD_WITH_DROP_SHADOW_STYLE = {
    boxShadow: '0px 4px 12px 0px #00000026',
    ...(isMobile ? { paddingLeft: 16, paddingRight: 16 } : {}),
  }

  const catchupBookkeepingPrice = useCatchupBookkeepingPrice()

  useEffect(() => {
    if (annualPlan) onSelect(annualPlan)
  }, [onSelect, annualPlan])

  const productPrice = productPriceAsCurrency({
    hasAnnualCommitment,
    product: selectedProduct,
  })

  const totalDueToday = useMemo(() => {
    if (freeTrialEnabled) {
      return currency(0)
    }

    if (catchupBookkeepingPrice) {
      return productPrice.add(
        currency(catchupBookkeepingPrice.amount).subtract(
          currency(catchupBookkeepingPrice.amount).multiply(
            (catchUpBKCoupon?.percent_off ?? 0) / 100
          )
        )
      )
    }

    return productPrice
  }, [freeTrialEnabled, catchupBookkeepingPrice, productPrice, catchUpBKCoupon])

  useEffect(() => {
    if (selectedProduct?.metadata.tier) {
      track(
        `viewed ${selectedProduct?.metadata.tier}, ${isAnnual ? 'Annual Plan' : 'Monthly Plan'} option in choose-plan`
      )
    }
  }, [track, isAnnual, selectedProduct?.metadata.tier])

  const subText = useMemo(() => {
    if (freeTrialSearchParamValue === FREE_TRIAL_PROMO && freeTrialEnabled) {
      return `If you choose to purchase Heard after your free trial, your plan will cost ${formatCurrency(productPrice, { hideDecimalsIfZero: true })} ${
        isAnnual ? 'annually' : 'monthly'
      }`
    } else if (freeTrialEnabled) {
      return `${FREE_TRIAL_DAYS} days free then ${formatCurrency(productPrice, { hideDecimalsIfZero: true })} billed ${
        isAnnual ? 'annually' : 'monthly'
      }${catchupBookkeepingPrice ? ` plus a one-time ${formatCurrency(catchupBookkeepingPrice.amount, { hideDecimalsIfZero: true })} fee for catch up bookkeeping` : ''}`
    } else if (catchupBookkeepingPrice) {
      return `Then ${formatCurrency(productPrice, { hideDecimalsIfZero: true })} billed ${
        isAnnual ? 'annually' : 'monthly'
      }`
    }

    return `Billed ${isAnnual ? 'annually' : 'monthly'}`
  }, [
    freeTrialEnabled,
    freeTrialSearchParamValue,
    catchupBookkeepingPrice,
    isAnnual,
    productPrice,
  ])

  return (
    <Grid>
      <GridRowColumn>
        <Button
          rounded
          variant={isAnnual ? 'primary' : 'secondary'}
          style={{ display: 'inline' }}
          size={isMobile ? 'small' : 'medium'}
          onClick={() => {
            if (annualPlan) onSelect(annualPlan)
          }}
          id="annual-plan-toggle-button"
        >
          {hasAnnualCommitment ? 'Annual Payment' : 'Annual Plan'}{' '}
          {!hasAnnualCommitment && (
            <span
              className={isAnnual ? 'selected' : ''}
              id="annual-percent-savings-tag"
            >
              Best Value
            </span>
          )}
        </Button>
        <Button
          rounded
          variant={!isAnnual ? 'primary' : 'secondary'}
          style={{ display: 'inline', marginLeft: 8 }}
          size={isMobile ? 'small' : 'medium'}
          onClick={() => {
            if (monthlyPlan) onSelect(monthlyPlan)
          }}
        >
          {hasAnnualCommitment ? 'Monthly Payment' : 'Monthly Plan'}
        </Button>
      </GridRowColumn>
      <GridRowColumn>
        <Card style={CARD_WITH_DROP_SHADOW_STYLE} fullWidth>
          <Card.Content>
            <PlanDetailsSection
              stripeProducts={stripeProducts}
              selectedProduct={selectedProduct}
              freeTrialEnabled={freeTrialEnabled}
              basicPlanEnabled={basicPlanEnabled}
              basicPlanSearchParamValue={basicPlanSearchParamValue}
              isLateJoiner={isLateJoiner}
            />
            <CatchupBookkeepingSection
              selectedAnnualPlan={isAnnual}
              isBasicPlanType={basicPlanEnabled}
            />
            <Divider />
            <Grid>
              <Grid.Row verticalAlign="middle">
                <Grid.Column width={8}>
                  <Text as={isMobile ? 'h3' : 'h2'}>Total Due Today</Text>
                </Grid.Column>
                <Grid.Column width={8} textAlign="right">
                  <Text
                    as={isMobile ? 'h1' : 'display2'}
                    style={{ display: 'inline' }}
                  >
                    {formatCurrency(totalDueToday, {
                      hideDecimalsIfZero: true,
                    })}
                  </Text>
                  {!catchupBookkeepingPrice && (
                    <Text
                      as={isMobile ? 'h2' : 'h1'}
                      style={{ display: 'inline' }}
                    >
                      /{isAnnual ? 'yr' : 'mo'}
                    </Text>
                  )}
                </Grid.Column>
              </Grid.Row>
              <GridRowColumn textAlign="right" short>
                <Text color="green" as="bodyLg">
                  {subText}
                </Text>
              </GridRowColumn>
            </Grid>
          </Card.Content>
        </Card>
      </GridRowColumn>
    </Grid>
  )
}

export default StripeProductCard
